import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Trash2, Calendar, MapPin, Package, RefreshCw } from "lucide-react";
import { http, apiError } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const STATUSES = ["new", "contacted", "booked", "closed"];
const STATUS_CLS = { new: "bg-peach text-ink", contacted: "bg-olive-light text-olive", booked: "bg-gold text-cream", closed: "bg-cream-deep text-ink-soft" };

const InquiryCard = ({ q, onStatus, onDelete }) => (
  <article className="rounded-2xl border border-gold/20 bg-white p-5" data-testid={`inquiry-${q.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-serif text-2xl text-ink">{q.name}</p>
        <p className="text-xs text-ink-soft">{new Date(q.created_at).toLocaleString("en-IN")}</p>
      </div>
      <div className="flex items-center gap-2">
        <span className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-wider ${STATUS_CLS[q.status] || ""}`}>{q.event_type}</span>
        <Select value={q.status} onValueChange={(v) => onStatus(q.id, v)}>
          <SelectTrigger className="h-9 w-32 rounded-full text-xs capitalize" data-testid={`inquiry-status-${q.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
        </Select>
        <button onClick={() => onDelete(q.id)} className="grid h-9 w-9 place-items-center rounded-full border border-red-200 text-red-700 hover:bg-red-50" aria-label="Delete inquiry" data-testid={`inquiry-delete-${q.id}`}><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
    <div className="mt-4 grid gap-2 text-sm text-ink sm:grid-cols-2">
      <a href={`tel:${q.phone}`} className="flex items-center gap-2 hover:text-gold"><Phone className="h-4 w-4 text-gold" />{q.phone}</a>
      {q.email && <a href={`mailto:${q.email}`} className="flex items-center gap-2 break-all hover:text-gold"><Mail className="h-4 w-4 text-gold" />{q.email}</a>}
      {q.event_date && <p className="flex items-center gap-2"><Calendar className="h-4 w-4 text-gold" />{q.event_date}</p>}
      {q.location && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gold" />{q.location}</p>}
      {q.package && <p className="flex items-center gap-2"><Package className="h-4 w-4 text-gold" />{q.package}</p>}
    </div>
    {q.message && <p className="mt-4 rounded-xl bg-cream p-3 text-sm text-ink">{q.message}</p>}
  </article>
);

export const InquiriesPanel = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = () => http.get("/inquiries").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const onStatus = async (id, status) => {
    try {
      await http.patch(`/inquiries/${id}`, { status });
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, status } : x)));
    } catch (e) { toast.error(apiError(e)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try {
      await http.delete(`/inquiries/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
    } catch (e) { toast.error(apiError(e)); }
  };
  const shown = filter === "all" ? items : items.filter((x) => x.status === filter);

  return (
    <div data-testid="inquiries-panel">
      <div className="flex flex-wrap items-center gap-2">
        {["all", ...STATUSES].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`rounded-full border px-4 py-2 text-xs capitalize ${filter === s ? "border-gold bg-gold text-cream" : "border-gold/30 bg-white text-ink"}`} data-testid={`inquiry-filter-${s}`}>
            {s} ({s === "all" ? items.length : items.filter((x) => x.status === s).length})
          </button>
        ))}
        <button onClick={load} className="ml-auto grid h-9 w-9 place-items-center rounded-full border border-gold/30 bg-white" aria-label="Refresh" data-testid="inquiries-refresh"><RefreshCw className="h-4 w-4" /></button>
      </div>
      <div className="mt-6 space-y-4">
        {shown.length === 0 && <p className="rounded-2xl border border-dashed border-gold/30 p-10 text-center text-ink-soft" data-testid="inquiries-empty">No inquiries yet.</p>}
        {shown.map((q) => <InquiryCard key={q.id} q={q} onStatus={onStatus} onDelete={onDelete} />)}
      </div>
    </div>
  );
};
